const IPA_DIACRITIC_CHARS = new Set([
  "ː",
  "ˑ",
  "ˈ",
  "ˌ",
  "ˇ",
  "˙",
  "ˠ",
  "ˤ",
  "˞",
  "ʰ",
  "ʲ",
  "ʷ",
  "̃",
  "̥",
  "̩",
  "̯",
  "̹",
  "̜",
  "̟",
  "̠",
  "̝",
  "̞",
  "̘",
  "̙",
  "̚",
  "̤",
  "̰",
  "̪",
  "̺",
  "̻",
  "͡",
  "̆",
  "̈",
  "ʼ",
]);

const IPA_COMPOSITE_CLUSTERS = new Set([
  "aɪ",
  "aʊ",
  "ɔɪ",
  "eɪ",
  "oʊ",
  "əʊ",
  "ɪə",
  "eə",
  "ʊə",
  "ɔʏ",
  "ɔø",
  "aɪ̯",
  "tʃ",
  "dʒ",
  "ts",
  "dz",
  "pf",
  "ks",
  "ɡʒ",
  "ʃt",
  "tʰ",
  "dʰ",
  "ɾɾ",
]);

const sanitizePhonemeValue = (value) => {
  if (value === null || value === undefined) {
    return "";
  }
  const normalized = String(value).trim();
  return normalized === "0" ? "" : normalized;
};

export const normalizePhonemeEntries = (input) => {
  if (!input) {
    return [];
  }

  if (typeof input === "string") {
    return input
      .split(/\s+/)
      .map((symbol, index) => sanitizePhonemeValue(symbol))
      .filter(Boolean)
      .map((symbol, index) => ({
        id: `${symbol}-${index}`,
        symbol,
        status: "",
      }));
  }

  if (Array.isArray(input)) {
    return input
      .map((item, index) => {
        if (item && typeof item === "object" && !Array.isArray(item)) {
          const [symbol, status] = Object.entries(item)[0] || [];
          const normalizedSymbol = sanitizePhonemeValue(symbol);
          if (!normalizedSymbol) {
            return null;
          }
          return {
            id: `${normalizedSymbol}-${index}`,
            symbol: normalizedSymbol,
            status: sanitizePhonemeValue(status).toUpperCase(),
          };
        }

        if (typeof item === "string") {
          const normalizedSymbol = sanitizePhonemeValue(item);
          if (!normalizedSymbol) {
            return null;
          }
          return {
            id: `${normalizedSymbol}-${index}`,
            symbol: normalizedSymbol,
            status: "",
          };
        }

        return null;
      })
      .filter(Boolean);
  }

  return [];
};

export const combinePhonemeEntries = (entries) => {
  const combined = [];

  const mergeStatuses = (a = "", b = "") => {
    const statusA = (a || "").toUpperCase();
    const statusB = (b || "").toUpperCase();
    if (statusA === "X" || statusB === "X") {
      return "X";
    }
    if (statusA === "M" || statusB === "M") {
      return "M";
    }
    return statusA || statusB || "";
  };

  for (let i = 0; i < entries.length; i += 1) {
    const entry = entries[i];
    if (!entry || !entry.symbol) {
      continue;
    }

    const symbol = entry.symbol;
    if (
      symbol.length === 1 &&
      IPA_DIACRITIC_CHARS.has(symbol)
    ) {
      if (combined.length === 0) {
        combined.push({ ...entry });
      } else {
        const previous = combined[combined.length - 1];
        combined[combined.length - 1] = {
          ...previous,
          symbol: `${previous.symbol}${symbol}`,
        };
      }
      continue;
    }

    let aggregatedSymbol = symbol;
    let aggregatedStatus = entry.status || "";

    if (i + 1 < entries.length) {
      const nextEntry = entries[i + 1];
      if (
        nextEntry &&
        nextEntry.symbol &&
        nextEntry.symbol.length === 1 &&
        !IPA_DIACRITIC_CHARS.has(nextEntry.symbol)
      ) {
        const candidate = `${symbol}${nextEntry.symbol}`;
        if (IPA_COMPOSITE_CLUSTERS.has(candidate)) {
          aggregatedSymbol = candidate;
          aggregatedStatus = mergeStatuses(aggregatedStatus, nextEntry.status);
          i += 1;
        }
      }
    }

    combined.push({
      ...entry,
      symbol: aggregatedSymbol,
      status: aggregatedStatus,
    });
  }

  return combined;
};

export const entriesToText = (entries) =>
  entries.map((entry) => entry.symbol).filter(Boolean).join(" ");

export const sanitizePhonemeList = (input) =>
  combinePhonemeEntries(normalizePhonemeEntries(input)).map(
    (entry) => entry.symbol
  );

export const buildEntriesFromPairs = (pairs = [], valueKey, statusKey) => {
  const mapped = pairs.map((item) => {
    const value =
      typeof valueKey === "function" ? valueKey(item) : item?.[valueKey];
    const statusValue =
      typeof statusKey === "function" ? statusKey(item) : item?.[statusKey];

    if (value === undefined || value === null) {
      return null;
    }

    return {
      [sanitizePhonemeValue(value)]: sanitizePhonemeValue(statusValue),
    };
  });

  return combinePhonemeEntries(normalizePhonemeEntries(mapped));
};
